import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { CaretDownIcon } from "@phosphor-icons/react";
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { ChoiceFilters } from "./filters";
import { ChoiceRow } from "./row";
import type { ChoiceFilter, ChoiceItem } from "./types";

export type { ChoiceFilter, ChoiceItem } from "./types";

/**
 * The field that opens a picker: what is chosen now, the way the list shows it.
 */
export function ChoicePickerTrigger({
  id,
  leading,
  label,
  meta,
  placeholder,
  onClick,
  className,
}: {
  id?: string;
  leading?: ReactNode;
  label?: ReactNode;
  meta?: ReactNode;
  placeholder: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button
      id={id}
      type="button"
      variant="outline"
      className={cn(
        "h-auto min-h-9 flex-1 justify-between py-1.5 font-normal",
        className,
      )}
      aria-haspopup="dialog"
      onClick={onClick}
    >
      {label ? (
        <span className="flex min-w-0 items-center gap-2">
          {leading}
          <span className="truncate font-medium">{label}</span>
          {meta && (
            <span className="text-muted-foreground truncate text-xs">
              {meta}
            </span>
          )}
        </span>
      ) : (
        <span className="text-muted-foreground">{placeholder}</span>
      )}
      <CaretDownIcon className="text-muted-foreground h-4 w-4 shrink-0" />
    </Button>
  );
}

export interface ChoicePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /**
   * The dialog's body, mounted with the dialog's content — from the opening to
   * the end of the closing animation: its filters and its pending choice start
   * over on every opening, and unmounting is what stops whatever a row was
   * playing.
   */
  children: ReactNode;
}

/**
 * A choice out of a catalogue — voices, booking software — in a dialog: a search
 * box, filters, a list that can be walked with the arrows, and a choice that only
 * counts on « Valider ». Comparing three entries must not leave the form on the
 * third one when the dialog is dismissed.
 *
 * Usage: `<ChoicePicker open … title><ChoicePickerPanel … /></ChoicePicker>`,
 * the panel being where the caller's own state lives (see `VoicePicker`).
 */
export function ChoicePicker({
  open,
  onOpenChange,
  title,
  description,
  children,
}: ChoicePickerProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl gap-0 p-0"
        // The search box: typing a name is the fastest way back to an entry.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.target as HTMLElement | null)
            ?.querySelector<HTMLInputElement>("input[type=search]")
            ?.focus();
        }}
      >
        <DialogHeader className="p-6 pb-4">
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export interface ChoicePickerPanelProps {
  /** The rows to show, already filtered and sorted by the caller. */
  items: ChoiceItem[];
  /** The row ticked when the dialog opened. */
  value?: string;
  search: string;
  onSearchChange: (search: string) => void;
  searchPlaceholder: string;
  filters?: ChoiceFilter[];
  /** Above the list, given the pending choice: a warning about it, say. */
  notice?: (pending: string | undefined) => ReactNode;
  /** What an empty list says; the reset button is offered under it. */
  emptyText: string;
  onResetFilters: () => void;
  /** Accessible name of the list. */
  listLabel: string;
  /**
   * A short catalogue's list takes the height of its rows. A long one keeps a
   * fixed height, so filtering does not make the dialog jump under the cursor.
   */
  fitContent?: boolean;
  onCancel: () => void;
  onConfirm: (id: string) => void;
}

/** Where each key moves the focus from row `index` of `count`. */
const ROW_KEYS: Record<string, (index: number, count: number) => number> = {
  ArrowDown: (index, count) => Math.min(index + 1, count - 1),
  ArrowUp: (index) => Math.max(index - 1, 0),
  Home: () => 0,
  End: (_, count) => count - 1,
};

export function ChoicePickerPanel({
  items,
  value,
  search,
  onSearchChange,
  searchPlaceholder,
  filters = [],
  notice,
  emptyText,
  onResetFilters,
  listLabel,
  fitContent = false,
  onCancel,
  onConfirm,
}: ChoicePickerPanelProps) {
  const { t } = useTranslation("choicePicker");
  const [pending, setPending] = useState(value);

  // Roving focus across the rows: one tab stop for the list, arrows to move,
  // and the row's own action reachable with Tab from the focused row.
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const checkedIndex = items.findIndex((item) => item.id === pending);
  const [focusIndex, setFocusIndex] = useState(-1);
  const activeIndex =
    focusIndex >= 0 && focusIndex < items.length
      ? focusIndex
      : Math.max(checkedIndex, 0);

  // The current choice may be anywhere in the list: start with it on screen.
  useEffect(() => {
    rowRefs.current[checkedIndex]?.scrollIntoView({ block: "nearest" });
    // Only on opening: following the tick afterwards would scroll under the mouse.
  }, []);

  // The ARIA radio pattern: the arrows move the focus *and* the choice — which
  // is only pending until « Valider », so walking the list commits nothing.
  const onRowKeyDown = (event: KeyboardEvent, index: number) => {
    const target = ROW_KEYS[event.key]?.(index, items.length);
    if (target === undefined) return;
    event.preventDefault();
    setFocusIndex(target);
    setPending(items[target].id);
    rowRefs.current[target]?.focus();
  };

  return (
    <>
      <ChoiceFilters
        search={search}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder}
        filters={filters}
      />

      {notice?.(pending)}

      <div
        role="radiogroup"
        aria-label={listLabel}
        className={cn(
          "overflow-y-auto border-t px-3 py-2",
          fitContent ? "max-h-[50vh]" : "h-[50vh]",
        )}
      >
        {items.length === 0 && (
          <div className="text-muted-foreground flex flex-col items-center gap-2 px-3 py-8 text-center text-sm">
            <p>{emptyText}</p>
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={onResetFilters}
            >
              {t("choicePicker.resetFilters")}
            </Button>
          </div>
        )}
        {items.map((item, index) => (
          <ChoiceRow
            key={item.id}
            item={item}
            checked={item.id === pending}
            tabIndex={index === activeIndex ? 0 : -1}
            buttonRef={(element) => {
              rowRefs.current[index] = element;
            }}
            onSelect={() => setPending(item.id)}
            onFocus={() => setFocusIndex(index)}
            onKeyDown={(event) => onRowKeyDown(event, index)}
          />
        ))}
      </div>

      <DialogFooter className="border-t px-6 py-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("choicePicker.cancel")}
        </Button>
        <Button
          type="button"
          disabled={!pending}
          onClick={() => pending && onConfirm(pending)}
        >
          {t("choicePicker.confirm")}
        </Button>
      </DialogFooter>
    </>
  );
}
